import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import ar from "../locales/legal/ar.json";
import de from "../locales/legal/de.json";
import es from "../locales/legal/es.json";
import fr from "../locales/legal/fr.json";
import hi from "../locales/legal/hi.json";
import id from "../locales/legal/id.json";
import it from "../locales/legal/it.json";
import ja from "../locales/legal/ja.json";
import ko from "../locales/legal/ko.json";
import nl from "../locales/legal/nl.json";
import pl from "../locales/legal/pl.json";
import ptBR from "../locales/legal/pt-BR.json";
import ru from "../locales/legal/ru.json";
import th from "../locales/legal/th.json";
import tr from "../locales/legal/tr.json";
import vi from "../locales/legal/vi.json";
import zhTW from "../locales/legal/zh-TW.json";

type LegalKind = "terms" | "privacy" | "refund";

interface LegalDocument {
  title: string;
  lead: string;
  sections: Array<{ title: string; paragraphs: string[] }>;
}

interface LegalCopy {
  updated: string;
  notice: string;
  documents: Record<LegalKind, LegalDocument>;
}

export const ENGLISH_LEGAL_COPY: LegalCopy = {
  updated: "Last updated: July 22, 2026",
  notice: "This product policy describes Raven Scribe’s current P1 service behavior. The operating legal entity, governing law and support address must be confirmed before commercial launch.",
  documents: {
    terms: {
      title: "Terms of Service",
      lead: "These terms apply when you use the Raven Scribe website, account, transcription, editing, export and billing services.",
      sections: [
        { title: "Accounts and permitted use", paragraphs: ["You are responsible for your account and activity. Use Raven Scribe only for media you own or are authorized to process, and do not use the service to violate law, privacy, copyright or another person’s rights."] },
        { title: "Content and processing", paragraphs: ["You retain ownership of your media and text. You grant us only the limited permission needed to store, process, transcribe, translate and deliver the content you request. SaaS processing may use Raven AIHub and Cloudflare infrastructure as described in the Privacy Policy."] },
        { title: "Service limits", paragraphs: ["Plans may limit file duration, file size, batch size, concurrent work, processing priority, storage retention and product features. We may reject unsafe files, unsupported formats, exhausted quota or abusive traffic. Generated transcripts and translations can contain errors and must be reviewed before consequential use."] },
        { title: "Billing, cancellation and liability", paragraphs: ["Paid access follows the price, billing period, tax and renewal information shown at checkout. You may cancel future renewal through the billing portal; access normally continues until the paid period ends. Mandatory consumer rights are not limited by these terms. To the extent permitted by law, the service is provided without guarantees of uninterrupted or error-free operation."] },
      ],
    },
    privacy: {
      title: "Privacy Policy",
      lead: "This policy explains what Raven Scribe processes, why it is needed, how long it is kept and what controls are available to you.",
      sections: [
        { title: "Data we process", paragraphs: ["We process account identifiers, verified email, login and security events, plan and payment references, product activity, uploaded media, extracted audio, transcripts, subtitles, edits, exports and support diagnostics. We do not need your Google or GitHub password."] },
        { title: "Purpose and service providers", paragraphs: ["We use this data to authenticate users, provide transcription and editing, enforce quota, deliver email, operate billing, prevent abuse and maintain the service. SaaS media and text may be processed by Raven AIHub and Cloudflare; payment and identity providers process the information needed for their part of the service."] },
        { title: "Storage, retention and security", paragraphs: ["Files are private by default and are read through authorization or expiring links. Retention depends on the active plan and is shown in the product. You can delete individual work, export account data or request account deletion. Transport uses HTTPS and production storage and key controls must be verified before launch."] },
        { title: "Training and your choices", paragraphs: ["Raven Scribe does not use customer content to train Raven-owned models. We will only make a broader no-training promise where Raven AIHub, Cloudflare and every applicable subprocesser contractually support it. Depending on your location, you may have rights to access, correct, export, restrict or delete personal data."] },
      ],
    },
    refund: {
      title: "Cancellation and Refund Policy",
      lead: "This policy distinguishes recurring Pro subscriptions from one-time minute packs and preserves any mandatory rights that apply where you live.",
      sections: [
        { title: "Subscription cancellation", paragraphs: ["Canceling a Pro subscription stops future renewal. Unless checkout terms or applicable law say otherwise, paid access and the current monthly quota remain available until the end of the paid period; unused recurring quota does not roll over."] },
        { title: "Refund requests", paragraphs: ["If the service was charged incorrectly, duplicated, unavailable due to a verified Raven Scribe failure, or a mandatory cooling-off or refund right applies, contact support with the order reference. Eligibility is assessed against checkout disclosures, service usage and applicable law."] },
        { title: "Minute packs", paragraphs: ["Minute packs are one-time purchases, do not auto-renew and have the validity period shown before purchase. Consumed or partially consumed packs are generally not refundable except where required by law or where a verified service failure prevented use."] },
        { title: "Processing a refund", paragraphs: ["Approved refunds are returned through the original payment method. Provider and bank processing times may vary. A refund can revoke the corresponding subscription entitlement or unused minute grant."] },
      ],
    },
  },
};

const SIMPLIFIED_CHINESE: LegalCopy = {
  updated: "最后更新：2026 年 7 月 22 日",
  notice: "本产品政策说明 Raven Scribe P1 当前的服务行为。正式商业发布前，仍需确认运营法律主体、适用法律和客服地址。",
  documents: {
    terms: {
      title: "服务条款",
      lead: "当你使用 Raven Scribe 网站、账户、转写、编辑、导出和计费服务时，本条款适用。",
      sections: [
        { title: "账户与允许用途", paragraphs: ["你应对自己的账户和操作负责。请仅处理你拥有或已获授权的媒体，不得利用本服务违反法律、隐私、著作权或他人合法权益。"] },
        { title: "内容与处理授权", paragraphs: ["你保留媒体和文字内容的所有权。你仅授予我们为完成所请求的存储、处理、转写、翻译和交付所必需的有限权限。SaaS 处理可能使用隐私政策中说明的 Raven AIHub 与 Cloudflare 基础设施。"] },
        { title: "服务限制", paragraphs: ["套餐可以限制文件时长、大小、批量数、并发数、处理优先级、保留期和功能。对于不安全文件、不支持格式、额度不足或滥用流量，我们可以拒绝处理。自动生成的转写和翻译可能存在错误，在重要场景使用前必须人工复核。"] },
        { title: "计费、取消与责任", paragraphs: ["付费服务以结账页显示的价格、周期、税费和续费信息为准。你可以通过账单门户取消后续续费，权益通常保留到已付费周期结束。本条款不限制法律强制规定的消费者权利。在法律允许范围内，我们不保证服务永不中断或完全无误。"] },
      ],
    },
    privacy: {
      title: "隐私政策",
      lead: "本政策说明 Raven Scribe 处理哪些数据、处理目的、保留方式以及你可以使用的控制能力。",
      sections: [
        { title: "我们处理的数据", paragraphs: ["我们处理账户标识、已验证邮箱、登录和安全事件、套餐与付款引用、产品活动、上传媒体、提取音频、转写稿、字幕、编辑内容、导出文件和支持诊断信息。我们不会获取你的 Google 或 GitHub 密码。"] },
        { title: "处理目的与服务提供方", paragraphs: ["这些数据用于身份验证、转写和编辑、额度执行、邮件交付、计费、防滥用和服务维护。SaaS 媒体与文本可能由 Raven AIHub 和 Cloudflare 处理；支付与登录提供方仅处理其服务所需的信息。"] },
        { title: "存储、保留与安全", paragraphs: ["文件默认私有，只能通过鉴权或短期限时链接读取。保留期取决于当前套餐，并在产品中展示。你可以删除单项内容、导出账户数据或申请删除账户。传输使用 HTTPS；正式上线前必须验证生产存储、密钥和备份控制。"] },
        { title: "模型训练与用户选择", paragraphs: ["Raven Scribe 不使用客户内容训练 Raven 自有模型。只有 Raven AIHub、Cloudflare 及所有适用子处理方均以合同支持时，我们才会作出更广泛的“不用于训练”承诺。根据所在地法律，你可能享有访问、更正、导出、限制或删除个人数据的权利。"] },
      ],
    },
    refund: {
      title: "取消与退款政策",
      lead: "本政策区分自动续费的 Pro 订阅和一次性分钟包，同时保留你所在地法律规定的强制性权利。",
      sections: [
        { title: "取消订阅", paragraphs: ["取消 Pro 订阅会停止后续续费。除非结账条款或适用法律另有规定，当前付费周期内仍可使用已购权益和当月额度；未使用的周期额度不会结转。"] },
        { title: "退款申请", paragraphs: ["如发生错误扣款、重复扣款、经确认的 Raven Scribe 服务故障，或你依法享有冷静期或退款权，请携带订单编号联系支持。我们会结合结账披露、服务使用情况和适用法律判断。"] },
        { title: "分钟包", paragraphs: ["分钟包属于一次性购买，不会自动续费，并按购买前展示的有效期使用。已经使用或部分使用的分钟包通常不退款，但法律强制要求或确认的服务故障导致无法使用的情形除外。"] },
        { title: "退款处理", paragraphs: ["批准的退款会原路退回，支付平台和银行到账时间可能不同。退款可以同时撤销对应订阅权益或尚未使用的分钟额度。"] },
      ],
    },
  },
};

const LOCALIZED_LEGAL_COPY: Record<string, LegalCopy> = {
  ar, de, es, fr, hi, id, it, ja, ko, nl, pl, "pt-BR": ptBR, ru, th, tr, vi, "zh-TW": zhTW,
};

export function legalCopyForLocale(locale: string): LegalCopy {
  return locale === "zh-CN"
    ? SIMPLIFIED_CHINESE
    : LOCALIZED_LEGAL_COPY[locale] ?? ENGLISH_LEGAL_COPY;
}

export function LegalPage() {
  const { kind = "terms" } = useParams<{ kind: LegalKind }>();
  const { i18n, t } = useTranslation();
  const normalized: LegalKind = kind === "privacy" || kind === "refund" ? kind : "terms";
  const copy = legalCopyForLocale(i18n.language);
  const document = copy.documents[normalized];

  return <div className="page legal-page">
    <header className="legal-hero"><p className="eyebrow">{t("legal.eyebrow")}</p><h1>{document.title}</h1><p>{document.lead}</p><span>{copy.updated}</span></header>
    <nav className="legal-tabs" aria-label={t("legal.navigation")}>
      {(["terms", "privacy", "refund"] as const).map((item) => <Link key={item} className={normalized === item ? "is-active" : ""} to={`/legal/${item}`}>{t(`legal.${item}`)}</Link>)}
    </nav>
    <div className="legal-layout"><main className="panel legal-document">{document.sections.map((section) => <section key={section.title}><h2>{section.title}</h2>{section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</section>)}</main><aside className="legal-notice"><strong>{t("legal.launchNotice")}</strong><p>{copy.notice}</p></aside></div>
  </div>;
}
